import { useContext, useEffect, useState } from "react";
import { ensure, getById } from "sprint-common-web";

import { EncodeSpecificationsDataContext } from "../../../../../common/data/lookup-data-providers/EncodeSpecificationsDataProvider";
import { EncodeSpecification } from "../../../../../common/types/EncodeSpecification";
import { EncodeSpecificationContentType } from "../../../../../common/types/EncodeSpecificationContentType";
import { MuxSpecification } from "../../../../../common/types/MuxSpecification";
import { useAdminAirlineApi } from "../../../../airline/common/api/useAdminAirlineApi";
import { SystemPlatformDetailsContext } from "../../context/SystemPlatformDetailsContext";
import { SystemPlatformDetailsItem } from "../../SystemPlatformDetailsItem";
import { EditSpecificationFormInputs } from "./EditSpecificationFormInputs";

export const useEditSpecificationForm = (
    systemId: string,
    encodeSpecificationContentType: EncodeSpecificationContentType
) => {
    const { updateSpecification } = useAdminAirlineApi();

    const { systemPlatformDetailsItem, updateSpecification: updateSpecificationItem } = useContext(
        SystemPlatformDetailsContext
    );
    const { encodeSpecifications } = useContext(EncodeSpecificationsDataContext);

    const itemToEdit = getItemToEdit(systemPlatformDetailsItem, encodeSpecificationContentType);

    const [availableEncodeSpecifications, setAvailableEncodeSpecifications] = useState<EncodeSpecification[]>([]);
    useEffect(() => {
        const availableEncodeSpecs = getAvailableEncodeSpecifications(
            encodeSpecifications,
            systemPlatformDetailsItem.encodeConfiguration.muxSpecification
        );

        setAvailableEncodeSpecifications(availableEncodeSpecs);
    }, [encodeSpecifications]);

    const editSpecification = async ({
        encodeSpecificationIds,
        defaultEncodeSpecificationId
    }: EditSpecificationFormInputs) => {
        await updateSpecification({
            airlineId: systemPlatformDetailsItem.airline.id,
            systemId,
            encodeSpecificationContentType,
            defaultEncodeSpecificationId,
            encodeSpecificationIds
        });

        const matchingEncodeSpecifications = encodeSpecifications.filter((item) =>
            encodeSpecificationIds.includes(item.id)
        );

        const defaultEncodeSpecification = getById(encodeSpecifications, defaultEncodeSpecificationId);

        updateSpecificationItem({
            encodeSpecificationContentType,
            defaultEncodeSpecificationId: defaultEncodeSpecification.id,
            defaultEncodeSpecificationName: defaultEncodeSpecification.name,
            encodeSpecifications: matchingEncodeSpecifications,
            isModified: true
        });
    };

    return {
        itemToEdit,
        encodeSpecificationOptions: availableEncodeSpecifications,
        editSpecification
    };
};

const getItemToEdit = (
    systemPlatformItem: SystemPlatformDetailsItem,
    encodeSpecificationContentType: EncodeSpecificationContentType
) => {
    const itemToEdit = systemPlatformItem.specifications.find(
        (specification) => specification.encodeSpecificationContentType === encodeSpecificationContentType
    );

    ensure(itemToEdit);

    return itemToEdit;
};

const getAvailableEncodeSpecifications = (
    encodeSpecifications: EncodeSpecification[],
    muxSpecification: MuxSpecification | undefined
) => {
    if (!muxSpecification) {
        throw new Error("Mux specification in configuration is missing.");
    }

    return encodeSpecifications.filter((encodeSpec) => encodeSpec.type === muxSpecification.encodeSpecificationType);
};
